import { useSuspenseQuery } from '@tanstack/react-query';
import React from 'react';

import { createPalette, PaletteButton } from '@gitbook/react-uikit';
import { Icon } from '@gitbook/spine-icons';
import { Description, Group, Heading } from '@gitbook/spine-react';
import { filterOutNullable } from '@gitbook/util-ts';

import { useNavigation } from '../../../../navigation';
import { apiQuery, useSiteContext } from '../../../../state';
import { showSettingsSection, siteSettingsSections } from './state';

export function SiteSettingsHeader(props: {
    headingId: string;
    heading: React.ReactNode;
    description: string;
    actions?: React.ReactNode;
}): React.ReactNode {
    const { headingId, heading, description, actions } = props;
    return (
        <div className="flex flex-wrap items-end justify-between gap-4">
            <Group>
                <Heading
                    level={1}
                    className="flex items-center gap-2 text-ui-heading-large"
                    id={headingId}
                >
                    <React.Suspense fallback={null}>
                        <SectionPaletteButton className="lg:hidden" />
                    </React.Suspense>
                    {heading}
                </Heading>
                <Description>{description}</Description>
            </Group>

            {actions}
        </div>
    );
}

/**
 * Hamburger menu shown at smaller screen sizes to access the settings sections.
 */
function SectionPaletteButton(props: { className?: string }) {
    const { navigate } = useNavigation();
    const { site } = useSiteContext();

    const { organizationId } = useSiteContext();
    const { data: organization } = useSuspenseQuery(
        apiQuery('getOrganizationById', { organizationId })
    );
    return (
        <PaletteButton
            kind="secondary"
            label="Settings"
            leadingIcon={Icon.Menu}
            iconOnly
            className={props.className}
            palette={() =>
                createPalette({
                    groups: siteSettingsSections.map((sections) => ({
                        key: 'options',
                        commands: sections
                            .filter(
                                (section) => showSettingsSection(site, organization)[section.id]
                            )
                            .map((section) => ({
                                key: section.id,
                                title: section.title,
                                icon: section.icon,
                                trigger: () => {
                                    navigate({
                                        current: {
                                            tab: {
                                                type: 'settings',
                                                section: section.id,
                                            },
                                        },
                                    });
                                },
                            }))
                            .filter(filterOutNullable),
                    })),
                })
            }
        />
    );
}
